import React from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// HomeChapterItem — draggable + tappable chapter row for main screen
function HomeChapterItem({ id, title, index, photoUrl, photoPosition, wordCount, type, layout, hasRecording, onEdit, photoEnhanced, onToggleEnhance }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
    position: 'relative',
  };
  const pPos = (pos) => {
    if (!pos) return 'center center';
    if (pos === 'top') return 'center top';
    if (pos === 'bottom') return 'center bottom';
    return 'center center';
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        background: isDragging ? '#fbf8f1' : '#ffffff',
        border: isDragging ? '2px solid #7c2d12' : '1px solid rgba(15,23,42,0.05)',
        borderRadius: 12, padding: '14px 16px',
        boxShadow: isDragging ? '0 8px 32px rgba(0,0,0,0.12)' : 'none',
        transition: 'all 0.15s ease',
        cursor: 'pointer',
      }} className={isDragging ? '' : 'chapter-card'}>
        <div {...attributes} {...listeners} className="drag-handle" style={{ cursor: 'grab', touchAction: 'none', padding: '6px 4px', flexShrink: 0, fontSize: '1rem', userSelect: 'none', WebkitUserSelect: 'none' }}>⠿</div>
        {photoUrl ? (
          // TB-076: <img loading="lazy"> inside an overflow-hidden wrapper so chapter
          // thumbnails defer their network fetch until they enter the viewport. The
          // outer <div> keeps the rounded border + sizing; the <img> handles the photo.
          <div style={{ width: 112, height: 112, flexShrink: 0, borderRadius: 10, border: '1px solid rgba(15,23,42,0.05)', overflow: 'hidden' }}>
            <img loading="lazy" src={photoUrl} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: pPos(photoPosition), display: 'block' }} />
          </div>
        ) : (
          <div style={{ width: 112, height: 112, flexShrink: 0, background: '#efebe0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, fontSize: '1.4rem', color: '#0f172a', fontFamily: "'Lora', serif", fontWeight: 500, border: '1px solid rgba(15,23,42,0.05)' }}>{index + 1}</div>
        )}
        <div onClick={onEdit} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 500, color: '#0f172a', fontFamily: "'Lora', serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
          <div className="chapter-card-meta" style={{ fontSize: '1.05rem', color: '#3c4653', marginTop: 1 }}>{type === 'memory' ? ' Bonus page' : wordCount + ' words'}{layout && layout !== 'photo-spread' ? ` · ${layout === 'classic' ? 'Classic' : layout === 'story-page' ? 'Photo Header' : layout === 'text-only' ? 'Text Only' : 'Photo Spread'}` : ''}{photoUrl ? ' · Has photo' : type !== 'memory' && layout !== 'text-only' ? ' · No photo' : ''}{hasRecording ? ' ·  QR' : ''}</div>
        </div>
        {photoUrl && onToggleEnhance && (
          photoEnhanced ? (
            <span className="chapter-enhance-btn" style={{
              fontSize: 11, color: '#5a7a4a', fontWeight: 600,
              padding: '3px 8px', background: 'rgba(90,122,74,0.1)',
              border: '1px solid rgba(90,122,74,0.25)', borderRadius: 6,
              flexShrink: 0, whiteSpace: 'nowrap',
            }}>
              ✓ Enhanced
            </span>
          ) : (
            <button
              className="chapter-enhance-btn"
              onClick={(e) => { e.stopPropagation(); onToggleEnhance(); }}
              style={{
                background: 'rgba(201,168,76,0.15)',
                border: '1px solid #7c2d12',
                borderRadius: 6,
                padding: '3px 8px',
                fontSize: 11,
                color: '#a08030',
                cursor: 'pointer',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
               Enhance
            </button>
          )
        )}
        <span onClick={onEdit} className="edit-link" style={{ fontSize: '1.05rem', flexShrink: 0 }}>Edit ›</span>
      </div>
    </div>
  );
}

// HomeReorderableList — draggable chapter list for main screen
function HomeReorderableList({ pages, stories, onReorder, onEditChapter, onToggleEnhance }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  );
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onReorder}>
      <SortableContext items={pages.map(p => p.storyId)} strategy={verticalListSortingStrategy}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {pages.map((page, idx) => {
            const story = stories.find(s => s.id === page.storyId);
            if (!story) return null;
            const wordCount = story.content ? story.content.split(/\s+/).length : 0;
            return (
              <HomeChapterItem
                key={page.storyId}
                id={page.storyId}
                title={story.title}
                index={idx}
                photoUrl={page.photoUrl}
                photoPosition={page.photoPosition}
                wordCount={wordCount}
                type={story.type}
                layout={page.layout}
                hasRecording={!!page.shareId}
                onEdit={() => onEditChapter(idx)}
                photoEnhanced={page.photoEnhanced}
                onToggleEnhance={onToggleEnhance ? () => onToggleEnhance(page) : undefined}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

// ChapterSearchList — isolates search state so typing doesn't re-render the book preview
export default function ChapterSearchList({ pages, stories, onReorder, onEditChapter, onToggleEnhance }) {
  const [search, setSearch] = React.useState('');
  const filtered = search ? pages.filter(p => {
    const story = stories.find(s => s.id === p.storyId);
    return story && story.title && story.title.toLowerCase().includes(search.toLowerCase());
  }) : pages;
  return (
    <>
      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: "0.85rem", color: "#b0a898", pointerEvents: "none" }}>🔍</span>
        <input
          type="text"
          className="chapter-search"
          placeholder="Search chapters..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <HomeReorderableList
        pages={filtered}
        stories={stories}
        onReorder={onReorder}
        onEditChapter={onEditChapter}
        onToggleEnhance={onToggleEnhance}
      />
    </>
  );
}
